import { useEffect, useState, useRef } from 'react';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { authService } from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';

export function MagicLinkCallbackPage() {
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [error, setError] = useState<string | null>(null);
    const { login } = useAuth();
    const hasValidated = useRef(false);

    useEffect(() => {
        const validateToken = async () => {
            // Prevent duplicate calls (React StrictMode runs effects twice)
            if (hasValidated.current) return;
            hasValidated.current = true;

            // Get token from URL query params
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');

            if (!token) {
                setStatus('error');
                setError('Invalid or missing token');
                return;
            }

            try {
                const response = await authService.validateToken(token);
                if (response.valid) {
                    login(response.email, response.token);
                    setStatus('success');
                    // Redirect to home after short delay
                    setTimeout(() => {
                        window.location.href = '/';
                    }, 1500);
                } else {
                    setStatus('error');
                    setError('Invalid or expired token');
                }
            } catch (err) {
                setStatus('error');
                const errorMessage =
                    err instanceof Error
                        ? err.message
                        : (err as { message?: string })?.message || 'Failed to validate token';
                setError(errorMessage);
            }
        };

        validateToken();
    }, [login]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.default',
                p: 2,
            }}
        >
            <Box sx={{ textAlign: 'center' }}>
                {status === 'loading' && (
                    <>
                        <CircularProgress size={48} sx={{ mb: 2 }} />
                        <Typography variant="h6">Verifying your magic link...</Typography>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Successfully signed in!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Redirecting you now...
                        </Typography>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Sign in failed
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                            {error}
                        </Alert>
                        <Button variant="contained" href="/">
                            Back to Login
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
}
